import type * as CSS from 'csstype';
import React, { useCallback, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { assertNever, isDefined } from '../../../utils/variables';
import { usePagination } from '../../hooks/use-pagination';
import { Icon } from '../icon/icon';
import { TextLoading } from '../skeleton';
import { Stack } from '../stack/stack';
import { Table } from '../table/table';
import { Props as TableCellProps } from '../table/table-cell';
import { HeaderCellProps } from '../table/table-header-cell';
import { TextBox } from '../text-box/text-box';
import { EllipsisContainer } from './ellipsis-container';
import { getNumberOfPages, Pagination } from './pagination';

export enum DataTableSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum DataTableSortType {
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  BOOLEAN = 'BOOLEAN',
}

type DataTableSortOptions = {
  sortOrder?: DataTableSortOrder;
  type: DataTableSortType;
  /**
   * Sorts by another key than the displayed one.
   */
  columnKey?: string;
};

type DataTableColumnConfig<T extends Record<string, any>> = {
  title?: string | React.ReactNode;
  formatter?: (value: any) => string;
  render?: (item: T) => React.ReactNode;
  unfilterable?: boolean;
  alignment?: 'left' | 'center' | 'right';
  alignColumnContent?: 'left' | 'center' | 'right';
  padding?: CSS.Properties['padding'];
  sortableBy?: DataTableSortOptions;
  monospaced?: 'montserrat' | 'roboto';
  width?: CSS.Properties['width'];
  shrink?: boolean;
  hidden?: boolean;
  /** When true, column content is only visible when the row is hovered. Use with width: 0 for a compact actions column. */
  showOnlyOnHover?: boolean;
};

export type DataTableColumns<T extends Record<string, any>> = {
  [K in keyof T]?: DataTableColumnConfig<T>;
} & {
  [key: string]: DataTableColumnConfig<T> | undefined;
};

export type DataTableColumnSorting = {
  columnKey: string;
  order: DataTableSortOrder;
};

type ServerPaginationProps = {
  /** Total number of items across all pages (from the server). */
  totalItems: number;
  /** Currently active page (1-based). */
  currentPage: number;
  /** Called when the user navigates to a different page. */
  onPageChange: (page: number) => void;
};

export type ServerSortingProps = {
  /** The currently active sort column key, or undefined if no sort is applied. */
  columnKey: string | undefined;
  /** The currently active sort order. */
  order: DataTableSortOrder | undefined;
  /** Called when the user clicks a sortable column header. */
  onSortChange: (columnKey: string, order: DataTableSortOrder) => void;
};

type Props<T extends Record<string, any>> = {
  columns: DataTableColumns<T>;
  items: T[];
  onListChanged?: (items: T[], filteredItems: T[]) => void;
  onSortingChanged?: (items: T[]) => void;
  flip?: boolean;
  onRowClicked?: (item: T) => void | Promise<void>;
  tableStyle?: {
    headerCell?: HeaderCellProps;
    rowCell?: TableCellProps;
  };
  highlightRow?: (item: T) => boolean;
  pageSize?: number;
  headerSeparator?: boolean;
  loading?: boolean;

  /**
   * Whether to use the fixed table layout algorithm or not. All cells with text content only
   * (string) will have ellipsis on overflow and title set to the text content when overflowed.
   *
   * Value is ignored if flip argument is set to true.
   */
  fixedLayout?: boolean;
  /**
   * Whether to disable text selection in the table.
   */
  disableTextSelect?: boolean;
  /**
   * Function to generate a unique key for each row. Defaults to using the array index.
   */
  getRowKey?: (item: T, index: number) => string;
  /**
   * When provided, the DataTable delegates pagination to the caller (server-side pagination).
   * Client-side paging is skipped; items are displayed as-is.
   */
  serverPagination?: ServerPaginationProps;
  /**
   * When provided, the DataTable delegates sorting to the caller (server-side sorting).
   * Client-side sorting is skipped; the caller is responsible for ordering items.
   */
  serverSorting?: ServerSortingProps;
  /**
   * Where to render the pagination controls relative to the table. Defaults to 'bottom'.
   */
  paginationPlacement?: 'top' | 'bottom';
};

type ActiveColumnFilters<T> = {
  [K in keyof T]: string | undefined;
};

const getDefaultSortingSettings = <T extends Record<string, string>>(
  columns: DataTableColumns<T>,
) => {
  const sortableColumns = Object.keys(columns)
    .filter((key) => columns[key]?.sortableBy?.sortOrder)
    .map(
      (key) =>
        ({
          columnKey: key,
          order: columns[key]?.sortableBy?.sortOrder,
        }) as DataTableColumnSorting,
    );

  if (sortableColumns.length > 1) {
    throw new Error('Only one column can have sortOrder option');
  }

  return sortableColumns?.[0];
};

const sortDates = <T extends Record<string, string>>(
  items: T[],
  columnKey: keyof T,
  order: DataTableSortOrder,
) => {
  return items
    .slice()
    .sort(
      order === DataTableSortOrder.ASC
        ? (a, b) => new Date(a[columnKey]).getTime() - new Date(b[columnKey]).getTime()
        : (a, b) => new Date(b[columnKey]).getTime() - new Date(a[columnKey]).getTime(),
    );
};

const sortStrings = <T extends Record<string, string>>(
  items: T[],
  columnKey: keyof T,
  order: DataTableSortOrder,
) => {
  return items
    .slice()
    .sort(
      order === DataTableSortOrder.DESC
        ? (a, b) => b[columnKey].localeCompare(a[columnKey])
        : (a, b) => a[columnKey].localeCompare(b[columnKey]),
    );
};

/**
 * Sorts numbers. Undefined values will be sorted last.
 */
const sortNumbers = <T extends Record<string, string>>(
  items: T[],
  columnKey: keyof T,
  order: DataTableSortOrder,
) => {
  const max = Number.MAX_SAFE_INTEGER / 2;
  const min = Number.MIN_SAFE_INTEGER / 2;

  return items
    .slice()
    .sort(
      order === DataTableSortOrder.DESC
        ? (a, b) => Number(b[columnKey] ?? min) - Number(a[columnKey] ?? min)
        : (a, b) => Number(a[columnKey] ?? max) - Number(b[columnKey] ?? max),
    );
};

/**
 * Sorts numbers. Undefined values will be sorted last.
 */
const sortBooleans = <T extends Record<string, string>>(
  items: T[],
  columnKey: keyof T,
  order: DataTableSortOrder,
) => {
  return items
    .slice()
    .sort(
      order === DataTableSortOrder.DESC
        ? (a, b) => (a[columnKey] === b[columnKey] ? 0 : a[columnKey] ? 1 : -1)
        : (a, b) => (a[columnKey] === b[columnKey] ? 0 : a[columnKey] ? -1 : 1),
    );
};

function getSortFunction(type?: DataTableSortType) {
  if (!isDefined(type)) {
    return;
  }

  switch (type) {
    case DataTableSortType.DATE:
      return sortDates;
    case DataTableSortType.STRING:
      return sortStrings;
    case DataTableSortType.NUMBER:
      return sortNumbers;
    case DataTableSortType.BOOLEAN:
      return sortBooleans;
    default:
      assertNever(type);
  }
}

const rotateOrder = (order: DataTableSortOrder): DataTableSortOrder =>
  order === DataTableSortOrder.ASC ? DataTableSortOrder.DESC : DataTableSortOrder.ASC;

const SortingIcon = ({ order }: { order: DataTableSortOrder }) => (
  <Icon icon={order === DataTableSortOrder.ASC ? 'caret-up' : 'caret-down'} />
);

const DataTable = <T extends Record<string, any>>({
  pageSize,
  columns,
  onListChanged,
  onRowClicked,
  onSortingChanged,
  items,
  fixedLayout,
  flip,
  headerSeparator,
  highlightRow,
  tableStyle,
  loading,
  disableTextSelect,
  getRowKey,
  serverPagination,
  serverSorting,
  paginationPlacement = 'bottom',
}: Props<T>) => {
  const [activeFilters, setActiveFilters] = React.useState<Partial<ActiveColumnFilters<T>>>({});
  const [sortingSettings, setSortingSettings] = React.useState<DataTableColumnSorting>();

  useEffect(() => {
    const defaultSortingSettings = getDefaultSortingSettings(columns);
    if (defaultSortingSettings) {
      setSortingSettings((prev) => {
        // Only update if values actually changed
        if (
          prev?.columnKey === defaultSortingSettings.columnKey &&
          prev?.order === defaultSortingSettings.order
        ) {
          return prev;
        }
        return defaultSortingSettings;
      });
    }
  }, [columns]);

  const onSort = useCallback(
    (columnKey: string) => {
      if (serverSorting) {
        const order: DataTableSortOrder =
          columnKey === serverSorting.columnKey && serverSorting.order
            ? rotateOrder(serverSorting.order)
            : DataTableSortOrder.ASC;
        serverSorting.onSortChange(columnKey, order);
        return;
      }
      const order: DataTableSortOrder =
        columnKey === sortingSettings?.columnKey
          ? rotateOrder(sortingSettings?.order)
          : DataTableSortOrder.ASC;
      setSortingSettings({ columnKey: columnKey, order: order });
    },
    [serverSorting, sortingSettings],
  );

  // This generates the following error:
  // "Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'never'."
  // Code compiles and works, and the usage hinders columnKey to ever be 'never'.
  const resolveRowValue = useCallback(
    (
      columnKey: keyof T,
      item: T,
      // @ts-ignore
    ) => columns[columnKey]?.formatter?.(item[columnKey]) ?? String(item[columnKey]),
    [columns],
  );

  // Filter items based on active filters - pure derivation, no state
  const filteredItems = useMemo(() => {
    const filters = Object.entries(activeFilters).filter(([, v]) => v && v !== '') as [
      keyof T,
      string,
    ][];

    if (filters.length === 0) {
      return items;
    }

    return items.filter((item) => {
      // Keep item if it matches ALL active filters
      return filters.every(([key, filterValue]) =>
        resolveRowValue(key, item).toLowerCase().includes(filterValue.toLowerCase()),
      );
    });
  }, [items, activeFilters, resolveRowValue]);

  // Compute filtered-out items for onListChanged callback
  const filteredOutItems = useMemo(() => {
    return items.filter((item) => !filteredItems.includes(item));
  }, [items, filteredItems]);

  // Sort filtered items - pure derivation, no state
  const refinedItems = useMemo(() => {
    // When server sorting is active, items are already ordered by the server
    if (serverSorting) {
      return filteredItems;
    }

    if (!sortingSettings) {
      return filteredItems;
    }

    const sortableBy = columns[sortingSettings.columnKey]?.sortableBy;
    if (!sortableBy?.type) {
      return filteredItems;
    }

    const sortFunction = getSortFunction(sortableBy.type);
    const key = (sortableBy.columnKey ?? sortingSettings.columnKey) as keyof T;

    return sortFunction?.([...filteredItems], key, sortingSettings.order) ?? [...filteredItems];
  }, [filteredItems, serverSorting, sortingSettings, columns]);

  const { currentPage, onPageChange, paginatedItems } = usePagination(
    pageSize,
    refinedItems,
    sortingSettings,
  );

  const renderedItems = useMemo(
    () =>
      serverPagination
        ? refinedItems
        : pageSize && currentPage && paginatedItems
          ? paginatedItems
          : refinedItems,
    [serverPagination, refinedItems, pageSize, currentPage, paginatedItems],
  );

  // Row key function
  const rowKeyFn = getRowKey ?? ((_, index) => String(index));

  // Call callbacks when derived values change
  useEffect(() => {
    onListChanged?.(filteredItems, filteredOutItems);
  }, [onListChanged, filteredItems, filteredOutItems]);

  // Call onSortingChanged on init, when sorting changes, or when items change
  useEffect(() => {
    if (!onSortingChanged) {
      return;
    }

    onSortingChanged(refinedItems);
  }, [onSortingChanged, sortingSettings, refinedItems]);

  if (flip) {
    return (
      <Component $disableTextSelect={disableTextSelect}>
        <Table>
          <Table.Body>
            {Object.keys(columns).map(
              (columnKey, columnIndex) =>
                columns[columnKey]?.hidden !== true && (
                  <Table.Row borderTop={false} key={columnIndex}>
                    <Table.Head>
                      <Table.HeaderCell
                        borderBottom={false}
                        key={columnIndex}
                        whiteSpace={'nowrap'}
                        {...tableStyle?.headerCell}
                      >
                        {columns[columnKey]?.title}
                      </Table.HeaderCell>
                    </Table.Head>
                    {renderedItems.map((item, columnIndex) => {
                      const column = columns[columnKey];
                      return (
                        <Table.Cell
                          className="notranslate"
                          key={columnIndex}
                          wrapping={'none'}
                          {...tableStyle?.rowCell}
                        >
                          {column?.render
                            ? column.render(item)
                            : React.isValidElement(item[columnKey] as React.ReactNode)
                              ? item[columnKey]
                              : resolveRowValue(columnKey as keyof T, item)}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                ),
            )}
          </Table.Body>
        </Table>
      </Component>
    );
  }

  const paginationNode =
    serverPagination && pageSize && getNumberOfPages(serverPagination.totalItems, pageSize) > 1 ? (
      <Pagination
        currentPage={serverPagination.currentPage}
        numberOfItems={serverPagination.totalItems}
        pageSize={pageSize}
        placement={paginationPlacement}
        onPageChange={serverPagination.onPageChange}
      />
    ) : !serverPagination &&
      pageSize &&
      currentPage &&
      getNumberOfPages(refinedItems.length, pageSize) > 1 ? (
      <Pagination
        currentPage={currentPage}
        numberOfItems={refinedItems.length}
        pageSize={pageSize}
        placement={paginationPlacement}
        onPageChange={(page) => onPageChange(page)}
      />
    ) : null;

  return (
    <Component $disableTextSelect={disableTextSelect}>
      {paginationPlacement === 'top' && paginationNode}
      <Table fixedLayout={fixedLayout}>
        <Table.Head>
          <Table.Row>
            {(Object.keys(columns) as (keyof T)[]).map((columnKey, index) => {
              const column = columns[columnKey];

              return (
                column?.hidden !== true && (
                  <Table.HeaderCell
                    borderBottom={headerSeparator ?? true}
                    key={index}
                    padding={column?.padding as TableCellProps['padding']}
                    shrink={column?.shrink}
                    textAlign={column?.alignColumnContent}
                    whiteSpace={'nowrap'}
                    width={column?.width}
                    onClick={
                      column?.sortableBy && typeof columnKey === 'string'
                        ? () => onSort(columnKey)
                        : undefined
                    }
                    {...tableStyle?.headerCell}
                  >
                    {isDefined(column?.sortableBy) ? (
                      <SortableColumnHeader direction="row" spacing={0.5}>
                        {typeof column?.title === 'string' ? (
                          <EllipsisContainer text={column.title} />
                        ) : (
                          column?.title ?? null
                        )}
                        {serverSorting
                          ? serverSorting.columnKey === columnKey &&
                            serverSorting.order && <SortingIcon order={serverSorting.order} />
                          : sortingSettings?.columnKey === columnKey && (
                              <SortingIcon order={sortingSettings.order} />
                            )}
                      </SortableColumnHeader>
                    ) : typeof column?.title === 'string' ? (
                      <EllipsisContainer text={column.title} />
                    ) : (
                      column?.title ?? null
                    )}

                    {!column?.unfilterable && (
                      <TextBox
                        onChange={(e) =>
                          setActiveFilters({ ...activeFilters, [columnKey]: e.target.value })
                        }
                      />
                    )}
                  </Table.HeaderCell>
                )
              );
            })}
          </Table.Row>
        </Table.Head>
        {loading ? (
          <Table.Body>
            {Array.from({ length: pageSize ?? 5 }).map((_, rowIndex) => (
              <Table.Row key={rowIndex}>
                {Object.keys(columns).map((columnKey, colIndex) => {
                  const column = columns[columnKey];
                  if (column?.hidden === true) {
                    return null;
                  }
                  const widthPattern = [75, 55, 65, 45, 70, 50, 60];
                  const skeletonWidth =
                    widthPattern[(rowIndex * 3 + colIndex) % widthPattern.length];
                  return (
                    <Table.Cell key={colIndex} width={column?.width} {...tableStyle?.rowCell}>
                      <SkeletonBar style={{ width: `${skeletonWidth}%` }} />
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        ) : (
          <Table.Body>
            {renderedItems.map((item, index) => (
              <Table.Row
                key={rowKeyFn(item, index)}
                selected={highlightRow?.(item)}
                onClick={() => onRowClicked?.(item)}
              >
                {Object.keys(columns).map((columnKey, columnIndex) => {
                  const column = columns[columnKey];

                  if (column?.hidden === true) {
                    return null;
                  }

                  // Use render callback if provided
                  if (column?.render) {
                    return (
                      <Table.Cell
                        key={columnIndex}
                        padding={column?.padding as TableCellProps['padding']}
                        showOnlyOnHover={column?.showOnlyOnHover}
                        shrink={column?.shrink}
                        width={column?.width}
                        wrapping={'none'}
                        {...tableStyle?.rowCell}
                      >
                        <Stack
                          alignItems={
                            column?.alignment === 'left'
                              ? 'flex-start'
                              : column?.alignment === 'center'
                                ? 'center'
                                : 'flex-end'
                          }
                          justifyContent="center"
                          key={columnKey}
                          spacing={0.5}
                          {...(column?.padding ? { padding: column?.padding } : {})}
                        >
                          {column.render(item)}
                        </Stack>
                      </Table.Cell>
                    );
                  }

                  if (React.isValidElement(item[columnKey] as React.ReactNode)) {
                    return (
                      <Table.Cell
                        key={columnIndex}
                        padding={column?.padding as TableCellProps['padding']}
                        showOnlyOnHover={column?.showOnlyOnHover}
                        shrink={column?.shrink}
                        width={column?.width}
                        wrapping={'none'}
                        {...tableStyle?.rowCell}
                      >
                        <Stack
                          alignItems={
                            column?.alignment === 'left'
                              ? 'flex-start'
                              : column?.alignment === 'center'
                                ? 'center'
                                : 'flex-end'
                          }
                          justifyContent="center"
                          key={columnKey}
                          spacing={0.5}
                          {...(column?.padding ? { padding: column?.padding } : {})}
                        >
                          {item[columnKey]}
                        </Stack>
                      </Table.Cell>
                    );
                  }

                  const value = resolveRowValue(columnKey as keyof T, item);
                  return (
                    <Table.Cell
                      className="notranslate"
                      key={columnIndex}
                      monospaced={column?.monospaced}
                      padding={column?.padding as TableCellProps['padding']}
                      showOnlyOnHover={column?.showOnlyOnHover}
                      shrink={column?.shrink}
                      textAlign={column?.alignment}
                      width={column?.width}
                      wrapping={'none'}
                      {...tableStyle?.rowCell}
                    >
                      {fixedLayout ? <EllipsisContainer text={value} /> : value}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        )}
      </Table>

      {paginationPlacement === 'bottom' && paginationNode}
    </Component>
  );
};

const Component = styled.div<{ $disableTextSelect?: boolean }>`
  overflow: auto;

  ${(props) =>
    props.$disableTextSelect &&
    css`
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    `}

  table {
    font-size: 12px;
  }

  tr:hover td[data-show-only-on-hover] {
    opacity: 1;
    overflow: visible;
  }
`;

const SortableColumnHeader = styled(Stack)`
  user-select: none;
  &:hover {
    cursor: pointer;
  }
`;

const SkeletonBar = styled(TextLoading)`
  height: 0.7em;
  margin: 0.3em 0;
`;

export { DataTable, Props as DataTableProps };
